import { flow, types } from 'mobx-state-tree'
import { TRANSACTION } from '../../../AppContants'
import { checkRemittanceStatus } from '../../../network/DomesticMoneyTransfer/checkRemittanceStatus'
import { getConvenienceFeeApi } from '../../../network/DomesticMoneyTransfer/getConvenienceFeeApi'
import {
  getDMRTransactions,
  getDmtTransactionsRequest,
} from '../../../network/DomesticMoneyTransfer/getDmtTransactionsApi'
import { getSplitTnxListApi } from '../../../network/DomesticMoneyTransfer/getSplitTnxListApi'
import { SUCCESS_CODE, transferAmount } from '../../../network/DomesticMoneyTransfer/transferAmount'
import {
  addCountryCode,
  calculateGrossAmount,
  calculateTotalConvinianceFee,
  delay,
  generateRandomNumber,
  getFormattedAmount,
  getMaskedAccountNumber,
  pkceGetCodeVerifier,
  returnDateString,
} from '../../../util/utill'
import { RootStore } from '../../store'

const Entity = types.model('Entity', {
  application: types.optional(types.number, 0),
  entityId: types.optional(types.string, ''),
  initiatingEntityId: types.optional(types.number, 0),
  type: types.optional(types.number, 0),
})

const Card = types.model('Card', {
  cardBin: types.optional(types.maybeNull(types.string), ''),
  scheme: types.optional(types.maybeNull(types.string), ''),
  type: types.optional(types.maybeNull(types.string), ''),
})

const Method = types.model('Method', {
  subType: types.optional(types.string, ''),
  type: types.optional(types.string, ''),
})

const feeTax = types.model('feeTax', {
  cgst: types.optional(types.number, 0),
  igst: types.optional(types.number, 0),
  sgst: types.optional(types.number, 0),
  tcs: types.optional(types.maybeNull(types.number), 0),
  tds: types.optional(types.number, 0),
  ugst: types.optional(types.number, 0),
})

const FeeAttributes = types.model('FeeAttributes', {
  maximumAmount: types.optional(types.number, 0),
  minimumAmount: types.optional(types.number, 0),
  type: types.optional(types.number, 0),
  value: types.optional(types.number, 0),
})

const Fee = types.model('Fee', {
  feeAttributes: types.array(FeeAttributes),
  feeTax: types.optional(feeTax, {}),
  merchantShare: types.optional(types.string, ''),
  totalConvenienceFee: types.optional(types.number, 0),
})

const ConvenienceFeeDetails = types.model('ConvenienceFeeDetails', {
  card: types.optional(Card, {}),
  fee: types.optional(Fee, {}),
  method: types.optional(Method, {}),
})

const Error = types.model('Error', {
  code: types.optional(types.maybeNull(types.string), ''),
  message: types.optional(types.maybeNull(types.string), ''),
})

const Transaction = types.model('Transaction', {
  transactionId: types.optional(types.maybeNull(types.string), ''),
  rrn: types.optional(types.maybeNull(types.string), ''),
  amount: types.optional(types.maybeNull(types.number), 0),
  charges: types.optional(types.maybeNull(types.number), 0),
  timestamp: types.optional(types.maybeNull(types.string), ''),
  status: types.optional(types.maybeNull(types.string), ''),
  error: types.optional(types.maybeNull(Error), {}),
})

const TransactionResponse = types.model('TransactionResponse', {
  beneficiaryAccountNumber: types.optional(types.maybeNull(types.string), ''),
  beneficiaryBankName: types.optional(types.maybeNull(types.string), ''),
  remitterMobile: types.optional(types.maybeNull(types.string), ''),
  rrn: types.optional(types.maybeNull(types.string), ''),
  transactionGrossAmount: types.optional(types.maybeNull(types.number), 0),
  transactionId: types.optional(types.maybeNull(types.string), ''),
  transactionNetAmount: types.optional(types.maybeNull(types.number), 0),
  transactionTime: types.optional(types.maybeNull(types.string), ''),
  beneficiaryName: types.optional(types.maybeNull(types.string), ''),
  transactionStatusReason: types.optional(types.maybeNull(types.string), ''),
  transactions: types.optional(types.array(Transaction), []),
})

const Charges = types.model('Charges', {
  value: types.optional(types.number, 0),
  cgst: types.optional(types.number, 0),
  igst: types.optional(types.number, 0),
  sgst: types.optional(types.number, 0),
  ugst: types.optional(types.number, 0),
  tds: types.optional(types.number, 0),
  serviceCharge: types.optional(types.number, 0),
  tcs: types.optional(types.maybeNull(types.number), 0),
})

const SplitTnx = types.model('SplitTnx', {
  amount: types.optional(types.string, ''),
  grossAmount: types.optional(types.string, ''),
  charge: types.optional(Charges, {}),
})

const SplittedTnxResponses = types.model('SplittedTnxResponses', {
  responseMessage: types.optional(types.string, ''),
  responseCode: types.optional(types.string, ''),
  responseData: types.optional(TransactionResponse, {}),
  errMessage: types.optional(types.string, ''),
  code: types.optional(types.string, ''),
  idempotentKey: types.optional(types.string, ''),
  index: types.optional(types.number, -1),
})

const TransactionDetails = types
  .model('TransactionDetails', {
    transactionId: types.optional(types.string, ''),
    amount: types.optional(types.string, ''),
    date: types.optional(types.string, ''),
    beneficiaryMobile: types.optional(types.string, ''),
    beneficiaryName: types.optional(types.string, ''),
    beneficiaryBank: types.optional(types.string, ''),
    transactionStatus: types.optional(types.string, ''),
    originalTransactionAmount: types.optional(types.string, ''),
    applicableCharges: types.optional(types.number, 0),
    applicableChargesForTransaction: types.optional(types.number, 0),
    applicableChargesForAmount: types.optional(types.string, ''),
    beneficiaryAccountNumber: types.optional(types.string, ''),
    entityDetails: types.optional(Entity, {}),
    feeDetails: types.array(ConvenienceFeeDetails),
    transactionResponse: types.optional(TransactionResponse, {}),
    transactionCode: types.optional(types.maybeNull(types.string), ''),
    splittedTnxs: types.array(SplitTnx),
    splittedTnxResponses: types.optional(types.array(SplittedTnxResponses), []),
  })
  .actions((self) => ({
    updateTransferredFund(amount: string) {
      self.originalTransactionAmount = amount
    },
    sendOtpToValidateForTransferringFund: flow(function* sendOtpToValidateForTransferringFund() {
      try {
        const response = yield pkceGetCodeVerifier(64)
        return response
      } catch (err) {
        RootStore.message.handleError(err)
        throw err
      }
    }),

    confirmFundTransfer: flow(function* confirmFundTransfer(body: any) {
      const initialTime = new Date().getTime()
      const idempotentKey = generateRandomNumber(16)
      try {
        let response: any = yield transferAmount(RootStore.moneyTransferSession.getTokens, body, idempotentKey)
        self.splittedTnxResponses.push({ ...response, idempotentKey, index: body?.index })
        console.log('htag response', response)
        if (response?.responseData?.transactions?.[0]?.status === 'PENDING') {
          try {
            response = yield (self as any).poolFundTransferStatus(0, idempotentKey)
          } catch (err) {
            console.log('error while fetching status', err)
          }
        }
        return { ...response, idempotentKey }
      } catch (err) {
        const errorTime = new Date().getTime()
        const modifiedErr = {
          ...err,
          errMessage: err?.message,
          idempotentKey,
          isTimeoutError: errorTime - initialTime > 58000,
          index: body?.index,
        }
        self.splittedTnxResponses.push(modifiedErr)
        RootStore.message.handleError(err)
        throw modifiedErr
      }
    }),
    poolFundTransferStatus: flow(function* poolFundTransferStatus(noOfRetry, idempotentKey) {
      if (noOfRetry < 3) {
        //pool
        yield delay(5000)
        const response = yield (self as any)?.fetchRemittanceStatus(idempotentKey)
        console.log('htag poolFundTransferStatus', response)
        if (response?.responseData?.transactions?.[0]?.status === 'PENDING') {
          return yield (self as any)?.poolFundTransferStatus(noOfRetry + 1, idempotentKey)
        }
        return response
      }
      throw {
        message: 'Maximum Retry Exceeded!',
      }
    }),
    clearSplittedTnxResponses: flow(function* clearSplittedTnxResponses() {
      self.splittedTnxResponses = [] as any
    }),

    fetchRemittanceStatus: flow(function* fetchRemittanceStatus(idempotentKey: string) {
      try {
        const response: any = yield checkRemittanceStatus(RootStore.moneyTransferSession.getTokens, idempotentKey)
        self.transactionStatus = response?.responseMessage
        self.transactionCode = response?.responseCode
        self.transactionResponse = response?.responseData
        self.transactionId = response?.responseData?.transactionId
        const index = self?.splittedTnxResponses?.findIndex((obj) => obj.idempotentKey === idempotentKey)
        if (index !== -1) {
          self.splittedTnxResponses[index] = {
            ...response,
            idempotentKey,
            index: self.splittedTnxResponses[index]?.index,
          }
          console.log('Updated object at index', index)
        }
        if (response?.responseCode !== SUCCESS_CODE) {
          self.transactionResponse.transactionStatusReason = response?.responseMessage ?? ''
        }
        return response
      } catch (err) {
        RootStore.message.handleError(err)
        throw err
      }
    }),
    getConvenienceFee: flow(function* getConvenienceFee(amount: string, screen: string) {
      try {
        const isInitiatedFromTransaction = screen === TRANSACTION
        const response = yield getConvenienceFeeApi(RootStore.moneyTransferSession.getTokens, amount, screen)
        self.entityDetails = response?.entity
        self.feeDetails = response?.feeDetails

        //----- NOTE: There would be only one array coming as confirmed ----
        if (isInitiatedFromTransaction) {
          const destructedFeeBody = response?.feeDetails?.[0]?.fee?.feeTax
          const GST_CHARGES = calculateGrossAmount(0, [
            destructedFeeBody?.cgst,
            destructedFeeBody?.igst,
            destructedFeeBody?.sgst,
            destructedFeeBody?.ugst,
            destructedFeeBody?.tds,
            destructedFeeBody?.tcs,
          ])
          self.applicableChargesForAmount = response?.amount
          self.applicableChargesForTransaction = response?.feeDetails?.[0]?.fee?.totalConvenienceFee + GST_CHARGES
        } else {
          self.applicableCharges = response?.feeDetails?.[0]?.fee?.totalConvenienceFee
        }

        return response
      } catch (err) {
        console.log(err)
        RootStore.message.handleError(err)
        throw err
      }
    }),
    getSplitTnxDetails: flow(function* getConvenienceFee(amount: string) {
      try {
        const response = yield getSplitTnxListApi(amount)
        console.log('response', response)
        self.applicableChargesForAmount = response?.amount
        self.applicableChargesForTransaction = calculateTotalConvinianceFee(response?.responseData?.transactions)
        self.splittedTnxs = response?.responseData?.transactions
        /* self.entityDetails = response?.entity
        self.feeDetails = response?.feeDetails

        //----- NOTE: There would be only one array coming as confirmed ----
        if (isInitiatedFromTransaction) {
          const destructedFeeBody = response?.feeDetails?.[0]?.fee?.feeTax
          const GST_CHARGES = calculateGrossAmount(0, [
            destructedFeeBody?.cgst,
            destructedFeeBody?.igst,
            destructedFeeBody?.sgst,
            destructedFeeBody?.ugst,
            destructedFeeBody?.tds,
            destructedFeeBody?.tcs,
          ])
          self.applicableChargesForAmount = response?.amount
          self.applicableChargesForTransaction = response?.feeDetails?.[0]?.fee?.totalConvenienceFee + GST_CHARGES
        } else {
          self.applicableCharges = response?.feeDetails?.[0]?.fee?.totalConvenienceFee
        }*/

        return response
      } catch (err) {
        console.log(err)
        RootStore.message.handleError(err)
        throw err
      }
    }),
    updateApplicableChargesOfTransaction(val: number) {
      self.applicableChargesForTransaction = val
    },
    getDmrTransactionsAsync: flow(function* getDmrTransactionsAsync(remitterMobileNo: any) {
      try {
        const reqBody = getDmtTransactionsRequest(
          RootStore.agent.agentInfo?.agentMobileNumber,
          RootStore.agent.agentInfo?.agentUserLoginId,
          remitterMobileNo,
        )
        const response = yield getDMRTransactions(RootStore.moneyTransferSession.getTokens, reqBody)
        return response
      } catch (err) {
        RootStore.message.handleError(err)
        throw err
      }
    }),
  }))
  .views((self) => ({
    transactionData() {
      return {
        'Failure Reason': self?.transactionResponse?.transactionStatusReason ?? '',
        'Gross Amount': `${getFormattedAmount(
          self?.transactionResponse?.transactionGrossAmount ||
          +(+self.originalTransactionAmount + +self?.applicableChargesForTransaction),
        )}`,
        'Date & Time': returnDateString(self.transactionResponse.transactionTime ?? ''),
        'Mobile No.': addCountryCode(self.transactionResponse.remitterMobile),
        'Beneficiary Name': self.transactionResponse?.beneficiaryName,
        'Account Number': getMaskedAccountNumber(self.transactionResponse?.beneficiaryAccountNumber ?? ''),
        'Bank IFSC': self.transactionResponse?.beneficiaryBankName,
      }
    },
  }))

const TransactionDetailsData = TransactionDetails.create({})

export { TransactionDetails, TransactionDetailsData }
